/**
 * Music outputs (W1-14, A§8). The transport drives one of them per play run through timeline segments:
 *  - `BufferMusicOutput`: one AudioBufferSourceNode per segment, started at exactly `(ctxStart, posStart)` with
 *    `playbackRate = rate` (pitch follows the rate) and stopped at `ctxEnd`. Splices (rate changes, loop edits)
 *    continue the same position, so they are seamless without fades; starts, seeks and loop wraps fade in over
 *    4 ms and halts fade out over 4 ms so a jump never clicks.
 *  - `SilentMusicOutput`: no music loaded; the transport still runs on the context clock.
 *  - the media-element path lives in streaming.ts.
 */

import type { Segment } from './timeline';

export type BeginKind = 'start' | 'splice' | 'wrap';
export type EndKind = 'splice' | 'wrap' | 'end';

/** A measurement of the element clock: music position `pos` is playing at context time `ctx`. */
export interface ClockSample {
  readonly ctx: number;
  readonly pos: number;
  /**
   * First sample after a discontinuity of the element clock (start, seek, loop wrap, rate change, stall recovery):
   * the transport averages the next samples and corrects the residual offset of its timeline (transport.ts).
   */
  readonly restarted: boolean;
}

export interface MusicOutput {
  readonly kind: 'buffer' | 'element' | 'silent';
  /** Play `seg` (from `ctxStart`, at `rate`). */
  begin(seg: Segment, how: BeginKind): void;
  /** Stop `seg` at context time `when` (wrap = crossfade into the next segment). */
  end(seg: Segment, when: number, how: EndKind): void;
  /** Forget a segment that has not started yet (a re-plan removed it). */
  cancel(seg: Segment): void;
  /** Stop everything now (short fade). */
  halt(): void;
  /**
   * Element path only: a fresh sample of the element clock (context time and music position), or null. Called on
   * every scheduler tick while playing.
   */
  observe(now: number): ClockSample | null;
  dispose(): void;
}

/** Fade length for starts, wraps and halts. */
export const MUSIC_FADE_SEC = 0.004;

export interface MusicContext {
  readonly currentTime: number;
  createBufferSource(): AudioBufferSourceNode;
  createGain(): GainNode;
}

interface Run {
  readonly src: AudioBufferSourceNode;
  readonly gain: GainNode;
  stopped: boolean;
}

export class BufferMusicOutput implements MusicOutput {
  readonly kind = 'buffer' as const;
  private readonly ctx: MusicContext;
  private readonly buffer: AudioBuffer;
  private readonly dest: AudioNode;
  private readonly runs = new Map<number, Run>();

  constructor(ctx: MusicContext, buffer: AudioBuffer, dest: AudioNode) {
    this.ctx = ctx;
    this.buffer = buffer;
    this.dest = dest;
  }

  /** Number of live source nodes (tests, diagnostics). */
  get liveRuns(): number {
    return this.runs.size;
  }

  begin(seg: Segment, how: BeginKind): void {
    if (!Number.isFinite(seg.ctxStart)) return;
    let when = seg.ctxStart;
    let offset = seg.posStart;
    if (offset < 0) {
      when += -offset / seg.rate;
      offset = 0;
    }
    if (offset >= this.buffer.duration || when >= seg.ctxEnd) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.buffer;
    src.playbackRate.value = seg.rate;
    const gain = this.ctx.createGain();
    src.connect(gain);
    gain.connect(this.dest);
    const run: Run = { src, gain, stopped: false };
    src.onended = () => {
      gain.disconnect();
      if (this.runs.get(seg.id) === run) this.runs.delete(seg.id);
    };
    // A fade-in only where the audio jumps into the middle of the song.
    const fade = how !== 'splice' && offset > 0;
    if (fade) {
      gain.gain.setValueAtTime(0, when);
      gain.gain.linearRampToValueAtTime(1, when + MUSIC_FADE_SEC);
    } else {
      gain.gain.value = 1;
    }
    src.start(when, offset);
    this.runs.set(seg.id, run);
    if (Number.isFinite(seg.ctxEnd)) this.end(seg, seg.ctxEnd, 'splice');
  }

  end(seg: Segment, when: number, how: EndKind): void {
    const run = this.runs.get(seg.id);
    if (run === undefined) return;
    if (how === 'wrap') {
      const p = run.gain.gain;
      p.cancelScheduledValues(when);
      p.setValueAtTime(1, when);
      p.linearRampToValueAtTime(0, when + MUSIC_FADE_SEC);
      run.src.stop(when + MUSIC_FADE_SEC);
    } else {
      run.src.stop(when);
    }
  }

  cancel(seg: Segment): void {
    const run = this.runs.get(seg.id);
    if (run === undefined) return;
    this.runs.delete(seg.id);
    run.stopped = true;
    try {
      run.src.stop();
    } catch {
      // Already stopped.
    }
    run.gain.disconnect();
  }

  halt(): void {
    const now = this.ctx.currentTime;
    for (const run of this.runs.values()) {
      if (run.stopped) continue;
      run.stopped = true;
      const p = run.gain.gain;
      p.cancelScheduledValues(now);
      p.setValueAtTime(p.value, now);
      p.linearRampToValueAtTime(0, now + MUSIC_FADE_SEC);
      try {
        run.src.stop(now + MUSIC_FADE_SEC);
      } catch {
        // Already stopped.
      }
    }
    this.runs.clear();
  }

  observe(): null {
    return null;
  }

  dispose(): void {
    this.halt();
  }
}

export class SilentMusicOutput implements MusicOutput {
  readonly kind = 'silent' as const;
  begin(): void {}
  end(): void {}
  cancel(): void {}
  halt(): void {}
  observe(): null {
    return null;
  }
  dispose(): void {}
}
